import React, { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { formatDocument, formatPhone, formatCEP } from "@/lib/validations";

export interface MaskedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  mask: 'document' | 'phone' | 'cep';
  onChange?: (value: string, maskedValue: string) => void;
}

export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ mask, onChange, value, ...props }, ref) => {
    // Formatar o valor controlado baseado na máscara
    const getFormattedValue = (val: string | number | readonly string[] | undefined) => {
      if (!val) return '';
      const strValue = String(val);
      
      switch (mask) {
        case 'document':
          return formatDocument(strValue);
        case 'phone':
          return formatPhone(strValue);
        case 'cep':
          return formatCEP(strValue);
        default:
          return strValue;
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      let maskedValue = rawValue;

      switch (mask) {
        case 'document':
          maskedValue = formatDocument(rawValue);
          break;
        case 'phone':
          maskedValue = formatPhone(rawValue);
          break;
        case 'cep':
          maskedValue = formatCEP(rawValue);
          break;
      }

      // Update the input value
      event.target.value = maskedValue;

      // Call onChange with both raw and masked values
      if (onChange) {
        const cleanValue = rawValue.replace(/\D/g, '');
        onChange(cleanValue, maskedValue);
      }
    };

    return (
      <Input
        {...props}
        ref={ref}
        inputMode={mask === 'document' || mask === 'phone' ? 'numeric' : props.inputMode}
        value={getFormattedValue(value)}
        onChange={handleChange}
      />
    );
  }
);

MaskedInput.displayName = "MaskedInput";